import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Grid,
  Stack,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  PageHeader,
  LoadingState,
  StatusChip,
  DetailCard,
  InfoRow,
} from '../components';
import {
  useReservation,
  useFulfillReservation,
  useReleaseReservation,
} from '../hooks/useStockReservations';
import { colors, formatDate } from '../constants';

export default function StockReservationDetail() {
  const { uuid } = useParams();
  const history = useHistory();
  const { data: reservation, isLoading } = useReservation(uuid);
  const fulfill = useFulfillReservation();
  const release = useReleaseReservation();
  const [toast, setToast] = useState(null);

  if (isLoading) return <LoadingState />;
  if (!reservation) {
    return (
      <>
        <PageHeader title="Not Found" backTo="/inventory-management/stock-reservations" />
        <Typography sx={{ color: colors.gray500 }}>Item not found</Typography>
      </>
    );
  }

  const handleFulfill = async () => {
    try {
      await fulfill.mutateAsync(reservation.uuid);
      setToast({ severity: 'success', message: 'Fulfilled' });
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Fulfill failed' });
    }
  };

  const handleRelease = async () => {
    try {
      await release.mutateAsync(reservation.uuid);
      history.push('/inventory-management/stock-reservations');
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Release failed' });
    }
  };

  return (
    <>
      <PageHeader
        title={reservation.orderReference || 'Reservation'}
        subtitle={`Order: ${reservation.orderUuid}`}
        backTo="/inventory-management/stock-reservations"
        actions={<StatusChip status={reservation.status} />}
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <DetailCard title="Details">
            <InfoRow label="Created" value={formatDate(reservation.createdAt)} />
            {reservation.expiresAt && <InfoRow label="Expires" value={formatDate(reservation.expiresAt)} />}
            {reservation.fulfilledAt && <InfoRow label="Fulfilled" value={formatDate(reservation.fulfilledAt)} />}
            <InfoRow label="Notes" value={reservation.notes} />
          </DetailCard>

          {reservation.status === 'pending' && (
            <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={handleFulfill}
                disabled={fulfill.isPending}
                sx={{ textTransform: 'none' }}
              >
                Fulfill
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelOutlinedIcon />}
                onClick={handleRelease}
                disabled={release.isPending}
                sx={{ textTransform: 'none' }}
              >
                Release
              </Button>
            </Stack>
          )}
        </Grid>

        <Grid item xs={12} md={7}>
          <DetailCard title="Reserved Items">
            {reservation.items?.map((item, idx) => (
              <Stack
                key={idx}
                direction="row"
                justifyContent="space-between"
                sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.sku}</Typography>
                  <Typography variant="caption" sx={{ color: colors.gray400 }}>{item.productType}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.quantity}</Typography>
              </Stack>
            ))}
          </DetailCard>
        </Grid>
      </Grid>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
